import { useState } from 'react'
import { data, useLoaderData } from 'react-router'
import { type SeoProps } from '#app/seo/seo.tsx'
import {
	buildAggregateOfferLd,
	buildFaqLd,
	buildHowToLd,
	buildItemListLd,
	buildWebApplicationLd,
} from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { type Route } from './+types/equipment-cost-estimator.ts'
import { EquipmentCategoryGrid } from './components/equipment-category-grid.tsx'
import { EquipmentFaq } from './components/equipment-faq.tsx'
import { EquipmentPriceTables } from './components/equipment-price-tables.tsx'
import { EquipmentProjectGuides } from './components/equipment-project-guides.tsx'
import {
	EQUIPMENT_CATEGORIES,
	EQUIPMENT_TYPES,
	STATIC_VENDOR_PRICES,
	type DbListing,
} from './equipment-cost-estimator.data.ts'
import { EQUIPMENT_FAQ_ITEMS } from './equipment-cost-estimator.faq.ts'

export async function loader({}: Route.LoaderArgs) {
	const listings = await prisma.rentalListing.findMany({
		where: {
			status: 'published',
			categories: {
				some: {
					slug: {
						in: [
							'tool-rental',
							'heavy-equipment-rental',
							'lawn-and-garden-rental',
						],
					},
				},
			},
		},
		select: {
			id: true,
			title: true,
			slug: true,
			hourlyRateCents: true,
			dailyRateCents: true,
			weeklyRateCents: true,
			monthlyRateCents: true,
			depositCents: true,
			attributes: true,
			updatedAt: true,
			subcategories: { select: { slug: true, name: true } },
			cities: { select: { id: true, name: true, slug: true } },
			ownerBusiness: {
				select: {
					name: true,
					slug: true,
					phone: true,
					website: true,
				},
			},
		},
		orderBy: { dailyRateCents: 'asc' },
	})

	const lastUpdated = listings.reduce<Date | null>((latest, l) => {
		const d = new Date(l.updatedAt)
		return !latest || d > latest ? d : latest
	}, null)

	return data({
		listings,
		lastUpdated: lastUpdated?.toISOString() ?? new Date().toISOString(),
	})
}

export const handle = {
	breadcrumb() {
		return {
			name: 'Equipment & Tool Rental Cost Estimator',
			url: '/tools/equipment-cost-estimator',
		}
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		const canonical = `${origin}/tools/equipment-cost-estimator`
		const title =
			'DFW Equipment & Tool Rental Costs | Compare Dallas-Fort Worth Rates'
		const description =
			'Compare rental costs for 40+ tools and equipment from DFW vendors. See daily, weekly, and monthly rates for everything from tillers to mini excavators across Dallas-Fort Worth.'

		const structuredData: Array<object | string> = []

		// FAQ schema
		structuredData.push(
			buildFaqLd(
				EQUIPMENT_FAQ_ITEMS as unknown as Array<{ q: string; a: string }>,
			),
		)

		// WebApplication schema
		structuredData.push(
			buildWebApplicationLd({
				name: 'DFW Equipment & Tool Rental Cost Estimator',
				url: canonical,
				description,
			}),
		)

		// HowTo schema
		structuredData.push(
			buildHowToLd({
				name: 'How to Rent Equipment in Dallas-Fort Worth',
				description:
					'A step-by-step guide to finding and renting the right equipment for your project in the DFW area.',
				steps: [
					{
						name: 'Identify the equipment you need',
						text: 'Browse categories like Lawn & Garden, Concrete & Masonry, Demolition, Lifts, Floor Care, Power Tools, and Generators to find the right tool for your project.',
					},
					{
						name: 'Compare rental prices',
						text: 'Review daily, weekly, and monthly rates from DFW vendors side by side. Real vendor listings appear alongside typical DFW rates for reference.',
					},
					{
						name: 'Check deposit and delivery requirements',
						text: 'Most rentals require a deposit ($50–$500+ depending on equipment size). Confirm delivery availability and fees with your chosen vendor.',
					},
					{
						name: 'Book your rental',
						text: 'Contact the vendor directly to confirm availability, schedule delivery or pickup, and finalize your rental agreement.',
					},
				],
			}),
		)

		// AggregateOffer from listing prices
		const listings = loaderData?.listings ?? []
		const prices = listings
			.map(
				(l: {
					weeklyRateCents?: number | null
					dailyRateCents?: number | null
				}) => l.weeklyRateCents ?? l.dailyRateCents,
			)
			.filter((p: number | null | undefined): p is number => p != null && p > 0)

		if (prices.length > 0) {
			structuredData.push(
				buildAggregateOfferLd({
					name: 'Equipment & Tool Rental in Dallas-Fort Worth',
					lowPrice: Math.min(...prices) / 100,
					highPrice: Math.max(...prices) / 100,
					offerCount: prices.length,
				}),
			)
		}

		// Per-equipment-type Product + AggregateOffer schemas
		const staticBySlug = new Map(
			STATIC_VENDOR_PRICES.map((sp) => [sp.equipmentSlug, sp]),
		)
		const typedListings = (listings as DbListing[]) ?? []

		for (const eqType of EQUIPMENT_TYPES) {
			const allPrices: number[] = []

			// Collect DB listing prices for this equipment type
			for (const listing of typedListings) {
				const hasMatch = listing.subcategories.some((sc) =>
					eqType.dbSubcategorySlugs.includes(sc.slug),
				)
				if (!hasMatch) continue
				const p = listing.dailyRateCents ?? listing.weeklyRateCents
				if (p && p > 0) allPrices.push(p)
			}

			// Include static price
			const staticPrice = staticBySlug.get(eqType.slug)
			if (staticPrice) {
				const p = staticPrice.dailyCents ?? staticPrice.weeklyCents
				if (p && p > 0) allPrices.push(p)
			}

			if (allPrices.length === 0) continue

			structuredData.push(
				JSON.stringify({
					'@context': 'https://schema.org',
					'@type': 'Product',
					name: `${eqType.name} Rental`,
					description: eqType.description,
					url: `${canonical}#${eqType.slug}`,
					category: 'Equipment Rental',
					offers: {
						'@type': 'AggregateOffer',
						lowPrice: (Math.min(...allPrices) / 100).toFixed(2),
						highPrice: (Math.max(...allPrices) / 100).toFixed(2),
						priceCurrency: 'USD',
						offerCount: allPrices.length,
						availability: 'https://schema.org/InStock',
						areaServed: {
							'@type': 'Place',
							name: 'Dallas-Fort Worth, TX',
						},
					},
				}),
			)
		}

		// ItemList schema for equipment categories
		structuredData.push(
			buildItemListLd({
				name: 'DFW Equipment Rental Categories',
				items: EQUIPMENT_CATEGORIES.map((cat) => ({
					name: cat.name,
					url: `${canonical}#category-${cat.slug}`,
				})),
			}),
		)

		return {
			title,
			description,
			canonical,
			keywords: [
				'equipment rental dallas',
				'tool rental dfw',
				'mini excavator rental dallas',
				'jackhammer rental',
				'floor sander rental',
				'scissor lift rental dallas',
				'pressure washer rental dfw',
				'stump grinder rental',
				'plate compactor rental',
				'equipment rental prices',
			],
			openGraph: {
				title,
				description,
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title,
				description,
			},
			structuredData,
		}
	},
}

export default function EquipmentCostEstimatorPage() {
	const { listings, lastUpdated } = useLoaderData<typeof loader>()
	const [activeCategory, setActiveCategory] = useState(
		EQUIPMENT_CATEGORIES[0]!.slug,
	)

	return (
		<div className="space-y-0">
			{/* Zone 1 — Category grid — white bg */}
			<div className="py-12">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<EquipmentCategoryGrid
						activeCategory={activeCategory}
						onCategoryChange={setActiveCategory}
					/>
				</div>
			</div>

			{/* Zone 2 — Price tables — muted bg */}
			<div className="bg-muted/30 py-12">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<EquipmentPriceTables
						dbListings={listings}
						activeCategory={activeCategory}
					/>
				</div>
			</div>

			{/* Zone 3 — Project guides — white bg */}
			<div className="py-12">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<EquipmentProjectGuides />
				</div>
			</div>

			{/* Zone 4 — FAQ — muted bg */}
			<div className="bg-muted/30 py-12">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<EquipmentFaq />
				</div>

				<div className="py-6">
					<p className="text-muted-foreground text-center text-xs">
						Prices and availability updated{' '}
						{new Date(lastUpdated).toLocaleDateString('en-US', {
							month: 'long',
							year: 'numeric',
						})}
						. Actual pricing may vary by vendor and delivery location.
					</p>
				</div>
			</div>
		</div>
	)
}
